/*
 * df-smart.js — Motor de inteligencia del tema.
 *
 * Expone `window.dfSmart` con:
 *   - .context  → perfil del visitante actualizado en vivo
 *   - .on()     → suscribirse a eventos ('ready', 'cart:updated', 'idle', 'scroll-deep', 'engage')
 *   - .track()  → log a dataLayer/GA4/Triple Whale
 *
 * Y dispara estos custom events sobre `document`:
 *   - 'df:smart:ready'        cuando el contexto inicial está listo
 *   - 'df:smart:engage:idle'  tras N segundos sin interacción
 *   - 'df:smart:engage:depth' al pasar el 60% del scroll
 *   - 'df:smart:engage:exit'  al detectar intent de salida (mouse out top)
 *
 * Diseñado para que las secciones Liquid lean `data-df-smart-X` o suscriban
 * a estos eventos y rehidraten su contenido.
 */
(function () {
  'use strict';

  // ==========================================================
  // Storage helpers (resistente a Safari private + cuota llena)
  // ==========================================================
  function safeRead(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeWrite(key, val) {
    try { localStorage.setItem(key, val); } catch (e) {}
  }
  function readJSON(key, fallback) {
    var raw = safeRead(key);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }
  function writeJSON(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {}
  }

  // ==========================================================
  // Visitor profile (persistente)
  // ==========================================================
  var profile = readJSON('df:profile', {
    firstSeen: Date.now(),
    visits: 0,
    lastVisit: 0,
    pagesViewed: 0,
    sessions: 0,
    purchases: 0,
    addToCart: 0,
    referrer_first: '',
    utm_first: null
  });

  // Update profile on each load
  var now = Date.now();
  var SESSION_GAP_MS = 30 * 60 * 1000; // 30 min de inactividad = nueva sesión
  var isNewSession = !profile.lastVisit || (now - profile.lastVisit) > SESSION_GAP_MS;
  if (isNewSession) {
    profile.sessions = (profile.sessions || 0) + 1;
    profile.visits = (profile.visits || 0) + 1;
  }
  profile.lastVisit = now;
  profile.pagesViewed = (profile.pagesViewed || 0) + 1;

  // Capture UTM first-touch (no overrides)
  var qs = new URLSearchParams(location.search);
  var utm = {
    source: qs.get('utm_source') || '',
    medium: qs.get('utm_medium') || '',
    campaign: qs.get('utm_campaign') || '',
    content: qs.get('utm_content') || ''
  };
  if (utm.source && !profile.utm_first) {
    profile.utm_first = utm;
  }
  if (!profile.referrer_first && document.referrer) {
    profile.referrer_first = document.referrer;
  }

  writeJSON('df:profile', profile);

  // ==========================================================
  // Live context — calculated each pageview
  // ==========================================================
  function detectReferrerType() {
    if (utm.source) return utm.source.toLowerCase();
    var ref = document.referrer || '';
    if (!ref) return profile.visits <= 1 ? 'direct' : 'returning';
    var host = '';
    try { host = new URL(ref).hostname; } catch (e) {}
    if (/google\./.test(host))       return 'google';
    if (/instagram\.|fb\.|facebook\.|messenger\./.test(host)) return 'instagram';
    if (/tiktok\./.test(host))       return 'tiktok';
    if (/pinterest\./.test(host))    return 'pinterest';
    if (/twitter\.|x\.com/.test(host)) return 'twitter';
    if (/youtube\./.test(host))      return 'youtube';
    if (host && host.indexOf(location.hostname) === -1) return 'other';
    return 'direct';
  }

  function detectDevice() {
    var w = window.innerWidth;
    if (w < 600) return 'mobile';
    if (w < 990) return 'tablet';
    return 'desktop';
  }

  function detectTimeOfDay() {
    var h = new Date().getHours();
    if (h < 6) return 'night';
    if (h < 12) return 'morning';
    if (h < 18) return 'afternoon';
    if (h < 22) return 'evening';
    return 'night';
  }

  function daysUntil(dateStr) {
    // dateStr 'YYYY-MM-DD'
    if (!dateStr) return null;
    var t = Date.parse(dateStr + 'T23:59:59');
    if (isNaN(t)) return null;
    return Math.ceil((t - Date.now()) / 86400000);
  }

  // Recent product handles (from PDP visits)
  var recent = (safeRead('df:recent') || '').split(',').filter(Boolean);

  // Wishlist count
  var wishlist = readJSON('df:wishlist', []) || [];

  // Visitor type
  var visitorType = 'first_time';
  if (profile.visits >= 3) visitorType = 'returning';
  else if (profile.visits === 2) visitorType = 'second_visit';

  // Engagement level — based on session counts + page count
  var engagement = 'cold';
  if (profile.sessions >= 3 || profile.pagesViewed >= 10) engagement = 'warm';
  if (profile.sessions >= 5 || profile.purchases > 0 || profile.addToCart > 2) engagement = 'hot';

  var context = {
    visitorType: visitorType,            // 'first_time' | 'second_visit' | 'returning'
    engagement: engagement,              // 'cold' | 'warm' | 'hot'
    referrer: detectReferrerType(),      // 'google' | 'instagram' | 'direct' | etc.
    device: detectDevice(),              // 'mobile' | 'tablet' | 'desktop'
    timeOfDay: detectTimeOfDay(),        // 'morning' | 'afternoon' | 'evening' | 'night'
    sessions: profile.sessions,
    visits: profile.visits,
    pagesViewed: profile.pagesViewed,
    recentProducts: recent,              // array de handles
    recentCount: recent.length,
    wishlistCount: wishlist.length,
    utm: utm,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    locale: navigator.language || 'es-ES',
    hour: new Date().getHours(),
    cart: null                           // se rellena via fetchCart()
  };

  // ==========================================================
  // Event bus
  // ==========================================================
  var listeners = {};
  function on(event, fn) {
    (listeners[event] = listeners[event] || []).push(fn);
  }
  function emit(event, detail) {
    (listeners[event] || []).forEach(function (fn) { try { fn(detail); } catch (e) {} });
    try { document.dispatchEvent(new CustomEvent('df:smart:' + event, { detail: detail })); } catch (e) {}
  }

  // ==========================================================
  // Cart state (live)
  // ==========================================================
  function fetchCart() {
    if (!window.fetch) return Promise.resolve(null);
    return fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        context.cart = {
          itemCount: cart.item_count || 0,
          totalPrice: cart.total_price || 0,
          items: (cart.items || []).map(function (it) { return { handle: it.handle, qty: it.quantity }; })
        };
        applyContextToDom();
        emit('cart:updated', context.cart);
        return cart;
      })
      .catch(function () { return null; });
  }

  // ==========================================================
  // Apply context to DOM via [data-df-smart-*] attributes
  // ==========================================================
  function applyContextToDom() {
    document.documentElement.setAttribute('data-df-visitor', context.visitorType);
    document.documentElement.setAttribute('data-df-engagement', context.engagement);
    document.documentElement.setAttribute('data-df-referrer', context.referrer);
    document.documentElement.setAttribute('data-df-device', context.device);
    document.documentElement.setAttribute('data-df-time', context.timeOfDay);
    if (context.cart) {
      document.documentElement.setAttribute('data-df-cart-items', context.cart.itemCount);
      document.documentElement.setAttribute('data-df-cart-state', context.cart.itemCount > 0 ? 'has-items' : 'empty');
    }
    if (context.wishlistCount > 0) document.documentElement.setAttribute('data-df-has-wishlist', '1');
    if (context.recentCount > 0)   document.documentElement.setAttribute('data-df-has-recent', '1');

    // Hidrata cualquier [data-df-smart-text="key"] con el valor del context
    document.querySelectorAll('[data-df-smart-text]').forEach(function (el) {
      var key = el.getAttribute('data-df-smart-text');
      var val = resolveContextKey(key);
      if (val != null) el.textContent = val;
    });
    document.querySelectorAll('[data-df-smart-show]').forEach(function (el) {
      var rule = el.getAttribute('data-df-smart-show');
      el.hidden = !evalRule(rule);
    });
    document.querySelectorAll('[data-df-smart-hide]').forEach(function (el) {
      var rule = el.getAttribute('data-df-smart-hide');
      if (evalRule(rule)) el.hidden = true;
    });
  }

  function resolveContextKey(key) {
    // Simple dotted accessor: 'cart.itemCount', 'visitorType', etc.
    return key.split('.').reduce(function (acc, k) {
      return acc == null ? acc : acc[k];
    }, context);
  }

  function evalRule(rule) {
    // Rules sintácticamente simples: 'visitorType=returning', 'cart.itemCount>0', 'referrer=instagram'
    var m = rule.match(/^([a-zA-Z0-9_.]+)\s*(=|!=|>|<|>=|<=)\s*(.+)$/);
    if (!m) return false;
    var actual = resolveContextKey(m[1]);
    var op = m[2];
    var expected = m[3].trim();
    if (/^[0-9.]+$/.test(expected)) expected = Number(expected);
    switch (op) {
      case '=':  return String(actual) === String(expected);
      case '!=': return String(actual) !== String(expected);
      case '>':  return Number(actual) > Number(expected);
      case '<':  return Number(actual) < Number(expected);
      case '>=': return Number(actual) >= Number(expected);
      case '<=': return Number(actual) <= Number(expected);
    }
    return false;
  }

  // ==========================================================
  // Engagement triggers
  // ==========================================================

  // 1) IDLE — sin interacción > N segundos
  var idleTimer = null;
  var idleSeconds = 25;
  var idleFired = false;
  function resetIdle() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(function () {
      if (idleFired) return;
      idleFired = true;
      emit('engage:idle', { seconds: idleSeconds });
    }, idleSeconds * 1000);
  }
  ['mousemove', 'scroll', 'keydown', 'touchstart', 'click'].forEach(function (ev) {
    document.addEventListener(ev, resetIdle, { passive: true });
  });
  resetIdle();

  // 2) SCROLL DEPTH — dispara al pasar 25, 50, 75, 90%
  var depthFired = {};
  function onScroll() {
    var h = document.documentElement;
    var max = h.scrollHeight - h.clientHeight;
    if (max <= 0) return;
    var pct = Math.round((window.pageYOffset / max) * 100);
    [25, 50, 75, 90].forEach(function (threshold) {
      if (!depthFired[threshold] && pct >= threshold) {
        depthFired[threshold] = true;
        emit('engage:depth', { percent: threshold });
      }
    });
  }
  window.addEventListener('scroll', throttle(onScroll, 250), { passive: true });

  function throttle(fn, ms) {
    var last = 0, timer = null;
    return function () {
      var n = Date.now();
      if (n - last >= ms) { last = n; fn(); }
      else { clearTimeout(timer); timer = setTimeout(function () { last = Date.now(); fn(); }, ms); }
    };
  }

  // 3) EXIT INTENT (desktop only) — mouse fuera del viewport por arriba
  if (context.device === 'desktop' && !document.documentElement.hasAttribute('data-df-no-exit')) {
    var exitFired = false;
    document.addEventListener('mouseleave', function (e) {
      if (exitFired) return;
      if (e.clientY < 5) {
        exitFired = true;
        emit('engage:exit', { reason: 'mouse-out-top' });
      }
    });
  }

  // ==========================================================
  // PREDICTIVE PREFETCH — hover en links de producto/colección
  // dispara fetch del HTML (cache lo guarda) para nav instantánea
  // ==========================================================
  var prefetched = {};
  function prefetch(url) {
    if (prefetched[url]) return;
    prefetched[url] = true;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  }
  function isInternalProductOrCollection(href) {
    if (!href) return false;
    return /^\/(products|collections|pages|blogs)\//.test(href);
  }
  // Hover (desktop) → prefetch
  document.addEventListener('mouseover', function (e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href');
    if (!isInternalProductOrCollection(href)) return;
    // Solo prefetch tras 80ms de hover continuo (filter accidentales)
    var t = setTimeout(function () { prefetch(href); }, 80);
    a.addEventListener('mouseout', function () { clearTimeout(t); }, { once: true });
  });
  // Touchstart (mobile) → prefetch inmediato
  document.addEventListener('touchstart', function (e) {
    var a = e.target.closest('a[href]');
    if (!a) return;
    var href = a.getAttribute('href');
    if (isInternalProductOrCollection(href)) prefetch(href);
  }, { passive: true });

  // ==========================================================
  // Tracking helper
  // ==========================================================
  function track(eventName, params) {
    var payload = Object.assign({ event: eventName, df_context: {
      visitorType: context.visitorType,
      engagement: context.engagement,
      referrer: context.referrer,
      device: context.device
    } }, params || {});
    try { if (window.dataLayer) window.dataLayer.push(payload); } catch (e) {}
    try { if (window.gtag) window.gtag('event', eventName, params || {}); } catch (e) {}
  }

  // ==========================================================
  // Track add-to-cart events automáticamente
  // ==========================================================
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-df-add], form[action*="/cart/add"] [type="submit"]');
    if (!btn) return;
    profile.addToCart = (profile.addToCart || 0) + 1;
    writeJSON('df:profile', profile);
    track('add_to_cart_click', {});
  });

  // ==========================================================
  // PUBLIC API
  // ==========================================================
  window.dfSmart = {
    context: context,
    profile: profile,
    on: on,
    emit: emit,
    track: track,
    refreshCart: fetchCart,
    resolveKey: resolveContextKey,
    evalRule: evalRule
  };

  // Inicializa contexto + cart en paralelo
  applyContextToDom();
  fetchCart();
  emit('ready', context);
  track('df_session_view', { visit_count: profile.visits, pages_viewed: profile.pagesViewed });
})();
